/**
 * useCreateOrder Hook
 *
 * Creates a new order with selected tickets using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { useOrderStore } from "@/stores";
import { errorTracking } from "@/lib/error-tracking";
import type { Order } from "@/generated/graphql";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const CreateOrderDocument = `
  mutation CreateOrder($input: CreateOrderInput!) {
    createOrder(input: $input) {
      id
      status
      totalAmount
      currency
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface OrderItemInput {
  ticketTypeId: string;
  quantity: number;
}

interface CreateOrderInput {
  eventId: string;
  items: OrderItemInput[];
}

interface UseCreateOrderResult {
  createOrder: (eventId: string, items: OrderItemInput[]) => Promise<Order>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Create a new order.
 *
 * Uses URQL mutation with automatic cache invalidation (see client.ts cacheExchange).
 * After successful creation, myOrders query is automatically refetched.
 */
export function useCreateOrder(): UseCreateOrderResult {
  const [result, executeMutation] = useMutation<
    { createOrder: Order },
    { input: CreateOrderInput }
  >(CreateOrderDocument);

  const { setCreatingOrder, setCreatedOrderId } = useOrderStore();

  const createOrder = useCallback(
    async (eventId: string, items: OrderItemInput[]): Promise<Order> => {
      setCreatingOrder(true);

      try {
        const response = await executeMutation({
          input: { eventId, items },
        });

        if (response.error) {
          throw new Error(
            response.error.graphQLErrors[0]?.message ||
              response.error.message ||
              "Failed to create order"
          );
        }

        if (!response.data?.createOrder) {
          throw new Error("No order returned from server");
        }

        const order = response.data.createOrder;
        setCreatedOrderId(order.id);

        errorTracking.addBreadcrumb({
          category: "order",
          message: "Order created",
          data: {
            orderId: order.id,
            eventId,
            ticketCount: items.reduce((sum, t) => sum + t.quantity, 0),
          },
        });

        return order;
      } catch (err) {
        const orderError =
          err instanceof Error ? err : new Error("Failed to create order");

        errorTracking.captureError(orderError, {
          tags: { feature: "orders", action: "createOrder" },
          extra: { eventId, items },
        });

        throw orderError;
      } finally {
        setCreatingOrder(false);
      }
    },
    [executeMutation, setCreatingOrder, setCreatedOrderId]
  );

  return {
    createOrder,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
